import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getGroup } from '../lib/firestore/groups';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { addGroupMember } from '../lib/firestore/groups';
import '../styles/GroupPreview.css';

const GroupPreview = () => {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get('notificationId');
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (groupId && user) {
      loadGroup();
      // Show join modal if coming from notification
      if (notificationId) {
        setShowJoinModal(true);
      }
    } else if (!user) {
      navigate('/login');
    }
  }, [groupId, user, notificationId]);

  const loadGroup = async () => {
    try {
      const groupData = await getGroup(groupId);
      setGroup(groupData);
    } catch (error) {
      console.error('Error loading group:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user || !groupId || joining) return;

    setJoining(true);
    try {
      // Add user to group
      await addGroupMember(groupId, {
        userId: user.uid,
        role: 'member',
        joinedAt: new Date(),
      });

      // Mark notification as read if exists
      if (notificationId) {
        try {
          const notificationRef = doc(db, 'notifications', notificationId);
          await updateDoc(notificationRef, {
            status: 'read',
            readAt: new Date(),
          });
        } catch (notifError) {
          console.error('Error updating notification:', notifError);
        }
      }

      // Navigate to full group page
      navigate(`/groups/${groupId}?joined=true`);
    } catch (error) {
      console.error('Error joining group:', error);
      alert('Failed to join group. Please try again.');
      setJoining(false);
    }
  };

  const handleDecline = async () => {
    if (notificationId) {
      try {
        const notificationRef = doc(db, 'notifications', notificationId);
        await updateDoc(notificationRef, {
          status: 'read',
          readAt: new Date(),
        });
      } catch (notifError) {
        console.error('Error updating notification:', notifError);
      }
    }
    setShowJoinModal(false);
    navigate('/home');
  };

  if (loading) {
    return (
      <div className="group-preview-container">
        <div className="loading">Loading group...</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-preview-container">
        <div className="error-message">Group not found</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  const firstLetter = group.name ? group.name.charAt(0).toUpperCase() : 'G';

  return (
    <div className="group-preview-container">
      <div className="group-preview-header">
        <button className="btn-back" onClick={() => navigate('/home')}>
          ← Back
        </button>
        <h1>Group Preview</h1>
      </div>

      <div className="group-preview-content">
        <div className="group-preview-image">
          {group.image ? (
            <img src={group.image} alt={group.name} />
          ) : (
            <div className="group-preview-avatar">
              <span>{firstLetter}</span>
            </div>
          )}
        </div>

        <h2 className="group-preview-name">{group.name}</h2>

        {group.description && (
          <div className="group-preview-description">
            <h3>About</h3>
            <p>{group.description}</p>
          </div>
        )}

        <div className="group-preview-restricted">
          <p>🔒 Join this group to see more details, events, and members.</p>
        </div>
      </div>

      {/* Join Modal */}
      {showJoinModal && (
        <div className="modal-overlay" onClick={handleDecline}>
          <div className="modal-content group-join-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Join Group</h2>
            <p>You've been invited to join <strong>{group.name}</strong>.</p>
            {group.description && (
              <div className="group-join-description">
                <p>{group.description}</p>
              </div>
            )}
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleDecline}
                disabled={joining}
              >
                Decline
              </button>
              <button
                className="btn btn-primary"
                onClick={handleJoin}
                disabled={joining}
              >
                {joining ? 'Joining...' : 'Accept & Join'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join Button (if modal not shown) */}
      {!showJoinModal && (
        <div className="group-preview-actions">
          <button
            className="btn btn-primary btn-large"
            onClick={() => setShowJoinModal(true)}
          >
            Join Group
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupPreview;



